// export const calculateItem = (x:InvoiceItem,co1?:number,co2?:number) =>
//   x.rate*x.quantity*(1 + (co1 ? co1 / 100 : 0))*(1 + (co2 ? co2 / 100 : 0))
// export const calculateItems = (xs:InvoiceItem[],co1?:number,co2?:number) =>
//   xs.reduce((agg,x) => agg+calculateItem(x,co1,co2),0)
export const discountRate = (x) => x === null ? null : Math.round(x / (100 + x) * 100 * 1000) / 1000;
// Selects which side of the dual-price model an invoice should be valued on.
// Any card payment forces card pricing; otherwise the cash side applies.
// Centralized so reports can't drift from each other on this rule.
export const invoicePaymentMethod = (invoice) => invoice.payments.some((p) => p.kind === 'card') ? 'card' : 'cash';
// Estimated card processing fees for a paid invoice. Mirrors the formula used by the
// Daily Transaction Report in src/server/web/routes/merchant-reports.fs (`x.amount *
// discountRate(merchant_non_cash_rate)`), applied only to card payments. Cash, check,
// write-off, and other payments incur no fee. Centralized so future fee-model changes
// (per-card-brand rates, etc.) update both reports at once.
export const computeProcessingFees = (invoice) => {
    const ratePct = discountRate(invoice.merchant.non_cash_rate);
    if (ratePct === null || ratePct === 0)
        return 0;
    const rate = ratePct / 100;
    return invoice.payments
        .filter((p) => p.kind === 'card')
        .reduce((sum, p) => sum + p.amount * rate, 0);
};
export const computeJobTotals = (invoice, jobId) => {
    const jobItems = invoice.items.filter((x) => x.job_id === jobId);
    const taxRate = invoice.merchant.sales_tax_rate / 100;
    const isTaxable = (x) => x.taxable && x.kind !== 'ntnf';
    const isNontaxable = (x) => !x.taxable || x.kind === 'ntnf';
    const side = (which) => {
        const taxable = jobItems.filter(isTaxable).reduce((a, x) => a + x[which].subtotal, 0);
        const nontaxable = jobItems.filter(isNontaxable).reduce((a, x) => a + x[which].subtotal, 0);
        const tax = taxable * taxRate;
        return { 'taxable_full': taxable, 'taxable': taxable, 'nontaxable_full': nontaxable, 'nontaxable': nontaxable, 'tax': tax, 'total': taxable + tax + nontaxable, 'discount': 0 };
    };
    return { 'totals_card': side('totals_card'), 'totals_noncard': side('totals_noncard') };
};
export const calculateItems = (method, tax, kind, invoice, jobIds) => {
    const items = invoice.items.filter((x) => kind === 'all' || x.kind === kind).filter((x) => jobIds === undefined || jobIds.includes(x.job_id));
    switch (method) {
        case 'cost':
            return items.reduce((agg, x) => agg + x.cost * x.quantity, 0);
        case 'card':
            {
                const { nontaxable, taxable } = items.reduce((agg, x) => {
                    const base = x.rate * x.quantity;
                    return (x.kind === 'ntnf'
                        ? { 'nontaxable': base + agg.nontaxable, 'taxable': agg.taxable }
                        : x.taxable
                            ? { 'nontaxable': agg.nontaxable, 'taxable': base * (1 + invoice.merchant.non_cash_rate * 0.01) + agg.taxable }
                            : { 'taxable': agg.taxable, 'nontaxable': base * (1 + invoice.merchant.non_cash_rate * 0.01) + agg.nontaxable });
                }, { 'nontaxable': 0, 'taxable': 0 });
                switch (tax) {
                    case 'taxed':
                        return taxable;
                    case 'untaxed':
                        return nontaxable;
                    case 'pretax':
                        return taxable + nontaxable;
                    case 'tax':
                        return taxable * (1 + invoice.merchant.sales_tax_rate * 0.01) - taxable;
                    case 'total':
                        return taxable * (1 + invoice.merchant.sales_tax_rate * 0.01) + nontaxable;
                }
            }
        case 'cash':
            {
                const { nontaxable, taxable } = items.reduce((agg, x) => {
                    const base = x.rate * x.quantity;
                    return (x.kind === 'ntnf'
                        ? { 'nontaxable': base + agg.nontaxable, 'taxable': agg.taxable }
                        : x.taxable
                            ? { 'nontaxable': agg.nontaxable, 'taxable': base + agg.taxable }
                            : { 'nontaxable': base + agg.nontaxable, 'taxable': agg.taxable });
                }, { 'nontaxable': 0, 'taxable': 0 });
                switch (tax) {
                    case 'taxed':
                        return taxable;
                    case 'untaxed':
                        return nontaxable;
                    case 'pretax':
                        return taxable + nontaxable;
                    case 'tax':
                        return taxable * (1 + invoice.merchant.sales_tax_rate * 0.01) - taxable;
                    case 'total':
                        return taxable * (1 + invoice.merchant.sales_tax_rate * 0.01) + nontaxable;
                }
            }
    }
};
export const calculateProRatedPrice = (price) => {
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const daysLeftInMonth = daysInMonth - today.getDate() + 1;
    return Math.round(((price * daysLeftInMonth) / daysInMonth) * 100) / 100;
};
export const calculateNextDueDate = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const month = String(nextMonth.getMonth() + 1).padStart(2, "0");
    const day = String(nextMonth.getDate()).padStart(2, "0");
    const year = nextMonth.getFullYear();
    return `${month}/${day}/${year}`;
};
export const toUSD = (value) => {
    const num = typeof value === "string" ? Number(value.replace(/[^0-9.-]+/g, "")) : value;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(num);
};
